import React from 'react';
import Icon from '../../../components/AppIcon';

const BehavioralAnalysisPanel = ({ data, isLoading }) => {
  const analysisItems = [
    {
      id: 1,
      title: 'Peak Access Hours',
      value: data?.peakHours,
      description: 'Highest activity between 9:00 AM - 11:00 AM',
      icon: 'Clock',
      trend: 'stable',
      color: 'text-accent'
    },
    {
      id: 2,
      title: 'Anomaly Detection Rate',
      value: `${data?.anomalyRate}%`,
      description: 'Unusual access patterns detected',
      icon: 'AlertTriangle',
      trend: data?.anomalyRate > 5 ? 'warning' : 'good',
      color: data?.anomalyRate > 5 ? 'text-warning' : 'text-success'
    },
    {
      id: 3,
      title: 'Off-Hours Access',
      value: data?.offHoursAccess,
      description: 'Access attempts outside business hours',
      icon: 'Moon',
      trend: data?.offHoursAccess > 10 ? 'warning' : 'good',
      color: data?.offHoursAccess > 10 ? 'text-warning' : 'text-muted-foreground'
    },
    {
      id: 4,
      title: 'Multiple Attempts',
      value: data?.multipleAttempts,
      description: 'Users with &gt;2 attempts in 5 minutes',
      icon: 'RefreshCw',
      trend: data?.multipleAttempts > 5 ? 'critical' : 'good',
      color: data?.multipleAttempts > 5 ? 'text-error' : 'text-success'
    }
  ];

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'good': return 'CheckCircle';
      case 'warning': return 'AlertCircle';
      case 'critical': return 'AlertTriangle';
      default: return 'Minus';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'good': return 'text-success';
      case 'warning': return 'text-warning';
      case 'critical': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 security-shadow-card">
        <div className="flex items-center justify-center h-40">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-primary rounded-full animate-pulse" />
            <span className="text-muted-foreground">Loading behavioral analysis...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 security-shadow-card">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground">Behavioral Analysis Summary</h3>
        <p className="text-sm text-muted-foreground">Security patterns and anomaly detection insights</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {analysisItems?.map((item) => (
          <div key={item?.id} className="bg-muted/20 border border-border/50 rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name={item?.icon} size={16} className="text-primary" />
              </div>
              <Icon 
                name={getTrendIcon(item?.trend)} 
                size={16} 
                className={getTrendColor(item?.trend)}
              />
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">{item?.title}</h4>
              <p className={`text-xl font-bold ${item?.color}`}>{item?.value}</p>
              <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: item?.description }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={20} className="text-accent mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Security Insights</h4>
            <p className="text-sm text-muted-foreground">
              Based on current patterns, consider implementing additional security measures during off-hours 
              and monitoring users with multiple failed attempts. Peak access times align with normal business operations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehavioralAnalysisPanel;